import React from 'react';
import { observer } from 'mobx-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';

import { useStore } from 'utils/hooks/store';
import { FolderType } from 'utils/api/modules/entities/entities.types';
import { UserRole } from 'core/constants';
import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import { FolderTreeItemKind } from 'stores/Folders/types';

import DocumentTreeItem from './DocumentTreeItem';
import FolderMenu from './FolderMenu';
import { useFolderDrag, useIsBlockedDropTarget } from './DragContext';
import {
  ContentWrapper,
  DragHandle,
  ExpandIcon,
  FolderItem,
  FolderName,
  EmptyFolderText,
  HANDLE_SLOT_WIDTH,
} from './styles';

const FolderTreeItem: React.FC<{
  folder: FolderType;
  depth: number;
}> = ({ folder, depth }) => {
  const { folders, session } = useStore();
  const { canReorder } = useFolderDrag();
  const canManageFolders = ADMIN_AND_MANAGERS.includes(session?.user?.role as UserRole);
  const isDraggable = canReorder && folders.reorderableUuids.has(folder.uuid);
  const isBlockedTarget = useIsBlockedDropTarget(folder.uuid, FolderTreeItemKind.Folder, folder.parent?.uuid ?? null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: folder.uuid,
    disabled: !isDraggable,
  });

  const isSelected = folders.selectedFolderId === folder.uuid;
  const isExpanded = folders.expandedFolderIds.has(folder.uuid);
  const hasSubfolders = folder.subfolders && folder.subfolders.length > 0;
  const hasDocuments = folder.documents && folder.documents.length > 0;
  const hasChildren = hasSubfolders || hasDocuments;

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    folders.toggleExpanded(folder.uuid);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <div ref={setNodeRef} style={style} data-row-uuid={folder.uuid}>
        <FolderItem
          data-testid={`folder-tree-item-${folder.uuid}`}
          $selected={isSelected}
          $depth={depth}
          $blockedTarget={isBlockedTarget}
          onClick={handleClick}
        >
          <DragHandle
            className={isDraggable ? 'drag-handle' : undefined}
            data-testid={isDraggable ? `folder-tree-drag-handle-${folder.uuid}` : undefined}
            $enabled={isDraggable}
            onClick={(e) => e.stopPropagation()}
            {...(isDraggable ? { ...attributes, ...listeners, 'aria-label': `Reorder ${folder.name}` } : {})}
          >
            {isDraggable && <DragIndicatorIcon fontSize="small" />}
          </DragHandle>
          <ExpandIcon $visible $expanded={isExpanded}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </ExpandIcon>
          <ContentWrapper>
            <Tooltip title={isDragging ? '' : folder.name} enterDelay={500}>
              <FolderName $selected={isSelected}>{folder.name}</FolderName>
            </Tooltip>
          </ContentWrapper>
          {canManageFolders && <FolderMenu folder={folder} />}
        </FolderItem>
      </div>

      {isExpanded && (
        <>
          {hasSubfolders &&
            folder.subfolders.map((subfolder) => (
              <FolderTreeItem key={subfolder.uuid} folder={subfolder} depth={depth + 1} />
            ))}
          {hasDocuments &&
            folder.documents.map((document) => (
              <DocumentTreeItem
                key={document.uuid}
                document={document}
                depth={depth + 1}
                currentFolderId={folder.uuid}
              />
            ))}
          {!hasChildren && (
            <EmptyFolderText style={{ paddingLeft: 8 + HANDLE_SLOT_WIDTH + (depth + 1) * 16 }}>
              No documents
            </EmptyFolderText>
          )}
        </>
      )}
    </>
  );
};

export default observer(FolderTreeItem);
